import { Dispatch, RefObject, SetStateAction, useCallback, useRef, useState } from 'react'
import {
  AttributeType,
  MoreFieldMappingItem,
} from 'src/interfaces/Template.interface'
import {
  getCategorySuggestionAsync,
  getMappingRulesAsync,
  getSelectedCategoryTreeAsync,
} from 'src/redux/actions/templateAndPricingActions'
import { useAppDispatch } from 'src/redux/storeHooks'
import { CategoryMappingChangeData } from '../CategoryMappingEngine'
import {
  buildListingProfileAiMappingData,
  CATEGORY_SUGGESTION_POLL_INTERVAL_MS,
  CATEGORY_SUGGESTION_POLL_MAX_ATTEMPTS,
  extractApiPayloadErrorMessage,
  extractCategorySuggestionErrorMessage,
  extractCategorySuggestionPayload,
  extractCategorySuggestionQueueId,
  extractMiscMappingResults,
  extractThunkFailureMessage,
  hasCategorySuggestionResult,
  hydrateCategoryMappingFromTemplatePayload,
  isCategorySuggestionQueuePending,
  ListingProfileAiProductRecipeRow,
  mergeMiscMappingSuggestions,
  sleepMs,
  validateListingProfileAiRequest,
} from '../utils/listingProfileAiAutoMapUtils'
import type { ListingProfileAiSuggestionPayload } from 'src/redux/actions/templateAndPricingActions'

export type ListingProfileInitialCategoryState = {
  categoryId: number | null
  categoryPath: string
  platformFieldId: number | null
}

type UseListingProfileAiAutoMapParams = {
  formikRef: RefObject<{ values?: { productRecipes?: ListingProfileAiProductRecipeRow[] } } | null>
  checkFormField: string
  destinationPlatformId: number | undefined | null
  sourcePlatformId: number | undefined | null
  showMoreMapping: boolean
  moreFieldMappingData: MoreFieldMappingItem[] | undefined
  hydratingCategory: boolean
  setHydratingCategory: (value: boolean) => void
  setCategoryUpdateData: (value: unknown) => void
  setInitialCategoryState: (value: ListingProfileInitialCategoryState) => void
  setEngineState: (value: CategoryMappingChangeData) => void
  setEngineKey: (updater: (prev: number) => number) => void
  setSelectedAttributes: (attributes: unknown[]) => void
  setPreloadedAttributeCatalog: (attributes: AttributeType[]) => void
  setMoreFieldMappingData: Dispatch<SetStateAction<MoreFieldMappingItem[]>>
  successToast: (message: string) => void
  warningToast: (message: string) => void
}

export const useListingProfileAiAutoMap = ({
  formikRef,
  checkFormField,
  destinationPlatformId,
  sourcePlatformId,
  showMoreMapping,
  moreFieldMappingData,
  hydratingCategory,
  setHydratingCategory,
  setCategoryUpdateData,
  setInitialCategoryState,
  setEngineState,
  setEngineKey,
  setSelectedAttributes,
  setPreloadedAttributeCatalog,
  setMoreFieldMappingData,
  successToast,
  warningToast,
}: UseListingProfileAiAutoMapParams) => {
  const dispatch = useAppDispatch()
  const aiAutoMapRequestIdRef = useRef(0)
  const [aiCategoryAutoMapLoading, setAiCategoryAutoMapLoading] = useState(false)
  const [aiMiscAutoMapLoading, setAiMiscAutoMapLoading] = useState(false)
  const [moreMappingAiApplyVersion, setMoreMappingAiApplyVersion] = useState(0)

  const resolveListingProfileAiRequestPayload = useCallback(() => {
    const formikValues = formikRef.current?.values
    const mapping_data = buildListingProfileAiMappingData(
      formikValues?.productRecipes,
      checkFormField
    )
    const validationError = validateListingProfileAiRequest({
      destinationPlatformId,
      sourcePlatformId,
      mappingData: mapping_data,
    })
    if (validationError) {
      return { error: validationError, payload: null }
    }
    return {
      error: null,
      payload: {
        platform_id: destinationPlatformId!,
        source_platform_id: sourcePlatformId!,
        mapping_data,
      },
    }
  }, [checkFormField, destinationPlatformId, formikRef, sourcePlatformId])

  const handleCategoryAiAutoMap = useCallback(async () => {
    const { error, payload } = resolveListingProfileAiRequestPayload()
    if (error || !payload) {
      warningToast(error ?? 'Unable to start AI Auto-Map.')
      return
    }

    const requestId = ++aiAutoMapRequestIdRef.current
    setAiCategoryAutoMapLoading(true)
    setHydratingCategory(true)

    try {
      let queueId: string | undefined
      let responsePayload: unknown = null

      for (let attempt = 0; attempt < CATEGORY_SUGGESTION_POLL_MAX_ATTEMPTS; attempt += 1) {
        if (requestId !== aiAutoMapRequestIdRef.current) return

        const requestBody: ListingProfileAiSuggestionPayload = queueId
          ? { ...payload, queue_id: queueId }
          : payload

        const result = await dispatch(getCategorySuggestionAsync(requestBody))
        if (requestId !== aiAutoMapRequestIdRef.current) return

        const thunkError = extractThunkFailureMessage(result)
        if (thunkError) {
          warningToast(thunkError)
          return
        }

        responsePayload = (result as { payload?: unknown }).payload

        if (hasCategorySuggestionResult(responsePayload)) {
          break
        }

        const bodyError = extractCategorySuggestionErrorMessage(responsePayload)
        if (bodyError) {
          warningToast(bodyError)
          return
        }

        if (isCategorySuggestionQueuePending(responsePayload)) {
          const nextQueueId = extractCategorySuggestionQueueId(responsePayload)
          if (nextQueueId) queueId = nextQueueId

          const isLastAttempt = attempt >= CATEGORY_SUGGESTION_POLL_MAX_ATTEMPTS - 1
          if (isLastAttempt) {
            warningToast(
              'Category suggestion is taking longer than expected. Please try again in a moment.'
            )
            return
          }

          await sleepMs(CATEGORY_SUGGESTION_POLL_INTERVAL_MS)
          continue
        }

        warningToast('No category suggestion was returned for the current filters.')
        return
      }

      if (!hasCategorySuggestionResult(responsePayload)) {
        warningToast('No category suggestion was returned for the current filters.')
        return
      }

      const catPayload = extractCategorySuggestionPayload(responsePayload)
      const applied = await hydrateCategoryMappingFromTemplatePayload(
        {
          fetchCategoryPathLabel: async (categoryId: number) => {
            const treeRes = await dispatch(
              getSelectedCategoryTreeAsync({ category_data_id: categoryId })
            )
            const pathPayload = (treeRes as { payload?: { api_name?: string }[] }).payload
            if (pathPayload?.length) {
              return pathPayload.map((it) => it.api_name).join(' > ')
            }
            return null
          },
          setCategoryUpdateData,
          setInitialCategoryState,
          setEngineState,
          setEngineKey,
          setSelectedAttributes,
          setPreloadedAttributeCatalog,
        },
        catPayload
      )

      if (!applied) {
        warningToast('No category suggestion was returned for the current filters.')
        return
      }

      successToast('AI category and attribute suggestions applied. Review before saving.')
    } catch (err) {
      console.error('Category AI Auto-Map failed:', err)
      warningToast('Category AI Auto-Map failed. Please try again.')
    } finally {
      if (requestId === aiAutoMapRequestIdRef.current) {
        setAiCategoryAutoMapLoading(false)
        setHydratingCategory(false)
      }
    }
  }, [
    dispatch,
    resolveListingProfileAiRequestPayload,
    setCategoryUpdateData,
    setEngineKey,
    setEngineState,
    setHydratingCategory,
    setInitialCategoryState,
    setSelectedAttributes,
    setPreloadedAttributeCatalog,
    successToast,
    warningToast,
  ])

  const handleMiscAiAutoMap = useCallback(async () => {
    if (!showMoreMapping || !(moreFieldMappingData?.length ?? 0)) {
      warningToast('No miscellaneous mapping fields are available for this channel.')
      return
    }

    const { error, payload } = resolveListingProfileAiRequestPayload()
    if (error || !payload) {
      warningToast(error ?? 'Unable to start AI Auto-Map.')
      return
    }

    const requestId = ++aiAutoMapRequestIdRef.current
    setAiMiscAutoMapLoading(true)

    try {
      const result = await dispatch(getMappingRulesAsync(payload))
      if (requestId !== aiAutoMapRequestIdRef.current) return

      const thunkError = extractThunkFailureMessage(result)
      if (thunkError) {
        warningToast(thunkError)
        return
      }

      const responsePayload = (result as { payload?: unknown }).payload
      const bodyError = extractApiPayloadErrorMessage(responsePayload)
      if (bodyError) {
        warningToast(bodyError)
        return
      }

      const results = extractMiscMappingResults(responsePayload)
      if (!results.length) {
        warningToast('No miscellaneous mapping suggestions were returned.')
        return
      }

      const { nextList, appliedCount } = mergeMiscMappingSuggestions(
        moreFieldMappingData ?? [],
        results
      )

      if (appliedCount === 0) {
        warningToast('Suggestions did not match any miscellaneous fields on this channel.')
        return
      }

      setMoreFieldMappingData(nextList)
      setMoreMappingAiApplyVersion((v) => v + 1)
      successToast(
        `AI applied ${appliedCount} miscellaneous field suggestion${appliedCount === 1 ? '' : 's'}. Review before saving.`
      )
    } catch (err) {
      console.error('Miscellaneous AI Auto-Map failed:', err)
      warningToast('Miscellaneous AI Auto-Map failed. Please try again.')
    } finally {
      if (requestId === aiAutoMapRequestIdRef.current) {
        setAiMiscAutoMapLoading(false)
      }
    }
  }, [
    dispatch,
    moreFieldMappingData,
    resolveListingProfileAiRequestPayload,
    setMoreFieldMappingData,
    showMoreMapping,
    successToast,
    warningToast,
  ])

  const categoryAiAutoMapDisabled =
    !destinationPlatformId ||
    !sourcePlatformId ||
    hydratingCategory ||
    aiMiscAutoMapLoading

  const miscAiAutoMapDisabled =
    !destinationPlatformId ||
    !sourcePlatformId ||
    aiCategoryAutoMapLoading ||
    hydratingCategory

  return {
    aiCategoryAutoMapLoading,
    aiMiscAutoMapLoading,
    moreMappingAiApplyVersion,
    handleCategoryAiAutoMap,
    handleMiscAiAutoMap,
    categoryAiAutoMapDisabled,
    miscAiAutoMapDisabled,
  }
}
